/**
 * Cliente HTTP de Trellis.
 *
 * El access token vive en memoria, nunca en localStorage: si viviera ahí,
 * cualquier XSS se lo llevaría. La sesión sobrevive a un refresh de la página
 * gracias a la cookie httpOnly del refresh token, que el JavaScript no puede leer.
 */

const BASE = import.meta.env.VITE_API_URL
  ? `${String(import.meta.env.VITE_API_URL).replace(/\/$/, '')}/api`
  : '/api';

let accessToken: string | null = null;
let onSessionLost: (() => void) | null = null;

/**
 * Programa dentro del cual está operando un superadmin de Dx Tech.
 *
 * Un superadmin no pertenece a ninguna institución: su token lleva `tenantId`
 * nulo. Para poder dar soporte dentro de un programa sin pedirle las
 * credenciales al cliente, la API acepta el header `x-tenant-slug` — y solo se
 * lo cree a un SUPERADMIN.
 *
 * Se resuelve acá y no en cada pantalla justamente para que ninguna se lo pueda
 * olvidar: si falta, la consola entera responde 403.
 */
let tenantSlugHeader: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function setTenantSlugHeader(slug: string | null): void {
  tenantSlugHeader = slug;
}

export function setSessionLostHandler(handler: () => void): void {
  onSessionLost = handler;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

import { isDemoMode, loadSnapshot, resolveDemo } from './demo';

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Slug del tenant, para que un superadmin pueda operar dentro de un programa. */
  tenantSlug?: string;
  signal?: AbortSignal;
  /** No intentar renovar la sesión si da 401. Lo usa el propio refresh. */
  skipRefresh?: boolean;
}

export interface SessionPayload {
  accessToken: string;
  user: {
    id: string;
    role: string;
    fullName: string | null;
    username: string;
    tenantSlug: string | null;
    patientId: string | null;
    mustChangePassword: boolean;
  };
}

/**
 * Renovación de sesión, con la promesa en curso compartida.
 *
 * El refresh token **rota en cada uso**: el servidor revoca el viejo al emitir
 * el nuevo. Eso es lo que permite detectar un token robado, pero significa que
 * dos llamadas simultáneas con el mismo token no pueden ganar las dos — la
 * segunda encuentra uno ya revocado y falla, tirando abajo la sesión que la
 * primera acababa de establecer.
 *
 * Pasa más seguido de lo que parece: React en modo estricto monta el provider
 * dos veces, y varias requests que expiran juntas disparan un refresh cada una.
 * Compartir la promesa hace que todas esperen el mismo resultado.
 *
 * Toda renovación tiene que pasar por acá — incluida la del arranque en
 * AuthProvider. Una llamada suelta a `/auth/refresh` se saltea este candado.
 */
let refreshInFlight: Promise<SessionPayload | null> | null = null;

async function refreshSession(): Promise<SessionPayload | null> {
  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!response.ok) return null;

      const data = (await response.json()) as Partial<SessionPayload>;
      if (!data.accessToken || !data.user) return null;

      accessToken = data.accessToken;
      return data as SessionPayload;
    } catch {
      return null;
    } finally {
      // Se libera en el próximo tick para que quienes ya estaban esperando
      // reciban este resultado y no arranquen otro refresh.
      setTimeout(() => {
        refreshInFlight = null;
      }, 0);
    }
  })();

  return refreshInFlight;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  /**
   * En simulación no se sale a la red.
   *
   * El corte va acá y en ningún otro lado: las pantallas no saben que existe un
   * modo demo, así que no hay forma de que se les olvide contemplarlo. Y como es
   * el único punto, tampoco hay riesgo de que un pedido se escape a la API real
   * con los datos de una institución.
   */
  /**
   * La autenticación nunca se simula, ni siquiera dentro de la recorrida.
   *
   * Todo lo demás en simulación se contesta con datos de mentira, y las
   * escrituras devuelven `{ id: 'demo-…', ok: true }` para que la recorrida no
   * se corte. Aplicado al ingreso, eso significaba entregarle las credenciales
   * —o el token de Microsoft— a un simulador que respondía "listo" sin sesión
   * adentro: la persona quedaba afuera sin entender por qué, y el flag vive en
   * `sessionStorage`, así que bastaba haber mirado la recorrida una vez en esa
   * pestaña. La puerta al mundo real no puede ser de utilería.
   */
  if (isDemoMode() && !path.startsWith('/auth/')) {
    await loadSnapshot();

    const resolved = resolveDemo<T>(path, options.method ?? 'GET');
    if (resolved === undefined) {
      throw new ApiError(
        404,
        'Esta pantalla no está incluida en la simulación. Ingresá con tu cuenta para verla.',
      );
    }
    return resolved;
  }

  const send = async (): Promise<Response> => {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (options.body !== undefined) headers['Content-Type'] = 'application/json';
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

    const tenantSlug = options.tenantSlug ?? tenantSlugHeader;
    if (tenantSlug) headers['x-tenant-slug'] = tenantSlug;

    return fetch(`${BASE}${path}`, {
      method: options.method ?? 'GET',
      headers,
      credentials: 'include',
      signal: options.signal,
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    });
  };

  let response = await send();

  if (response.status === 401 && !options.skipRefresh) {
    const renewed = await refreshSession();
    if (renewed) {
      response = await send();
    } else {
      accessToken = null;
      onSessionLost?.();
    }
  }

  if (response.status === 204) return undefined as T;

  const isJson = response.headers.get('content-type')?.includes('application/json');
  const payload = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    const error = (payload as { error?: { message?: string; code?: string; details?: unknown } })?.error;
    throw new ApiError(
      response.status,
      error?.message ?? `Error ${response.status}`,
      error?.code,
      error?.details,
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>(path, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'DELETE' }),
  refresh: refreshSession,
};

/**
 * Descarga un archivo autenticado. No se puede hacer con un `<a href>` normal
 * porque la ruta pide el token, y un enlace no manda cabeceras.
 */
export async function download(path: string, filename: string): Promise<void> {
  const response = await fetch(`${BASE}${path}`, {
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    credentials: 'include',
  });

  if (!response.ok) {
    throw new ApiError(
      response.status,
      response.status === 401
        ? 'Tu sesión venció. Volvé a entrar y probá de nuevo'
        : 'No se pudo descargar el archivo',
    );
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);

  /**
   * El enlace tiene que estar **en el documento** para que el clic dispare la
   * descarga: fuera del árbol, algunos navegadores lo ignoran en silencio y el
   * botón parece no hacer nada.
   *
   * Y la URL se libera después de un tiempo, no en la línea siguiente:
   * revocarla al instante corta la descarga antes de que el navegador termine
   * de leer el blob.
   */
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();

  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}
